import { useDomainMessage } from '../utils/domain/intl'
import { type BaseTemplateProps } from './NotificationTemplate'
import { ArConnectApiKeyCreatedNotifyAuthor } from './implementations/ArConnectApiKeyCreatedNotifyAuthor'
import { ArYodaRulesCreatedNotifyAuthor } from './implementations/ArYodaRulesCreatedNotifyAuthor'
import { StoreHoursUpdateFailure } from './implementations/StoreHoursUpdateFailure'
import { RecipeFileFailure } from './implementations/RecipeFileFailure'
import { ArConnectApiKeyExpiryNotifyKeyManagers } from './implementations/ArConnectApiKeyExpiryNotifyKeyManagers'
import { ArConnectApiKeyPublishedNotifyFollowers } from './implementations/ArConnectApiKeyPublishedNotifyFollowers'
import { ArConnectApiKeyAddedCancelNotifyFollowers } from './implementations/ArConnectApiKeyAddedCancelNotifyFollowers'
import { ArFulfillmentEventsPublishedNotifyFollowers } from './implementations/ArFulfillmentEventsPublishedNotifyFollowers'
import { ArFulfillmentEventsAddedReviewNotifyFollowers } from './implementations/ArFulfillmentEventsAddedReviewNotifyFollowers'
import { ArFulfillmentEventsAddedCancelNotifyFollowers } from './implementations/ArFulfillmentEventsAddedCancelNotifyFollowers'
import { ArYodaRulesPublishedNotifyFollowers } from './implementations/ArYodaRulesPublishedNotifyFollowers'
import { ArYodaRulesAddedCancelNotifyFollowers } from './implementations/ArYodaRulesAddedCancelNotifyFollowers'
import { ArFulfillmentEventsCreatedNotifyApprovers } from './implementations/ArFulfillmentEventsCreatedNotifyApprovers'
import { ArYodaRulesCreatedNotifyApprovers } from './implementations/ArYodaRulesCreatedNotifyApprovers'
import { ArYodaRulesAddedApprovalAndPublishNotifyFollowers } from './implementations/ArYodaRulesAddedApprovalAndPublishNotifyFollowers'
import { StoreHoursUpdateSuccess } from './implementations/StoreHoursUpdateSuccess'
import { RecipeFileReceived } from './implementations/RecipeFileReceived'
import { ArFulfillmentEventsCreatedNotifyAuthor } from './implementations/ArFulfillmentEventsCreatedNotifyAuthor'
import { ArConnectApiKeyAddedApprovalAndPublishNotifyFollowers } from './implementations/ArConnectApiKeyAddedApprovalAndPublishNotifyFollowers'
import { ArConnectApiKeyCreatedNotifyApprovers } from './implementations/ArConnectApiKeyCreatedNotifyApprovers'
import { OAuthApplicationRotation } from './implementations/OAuthApplicationRotation'
import { PartnerDocsUpdate } from './implementations/PartnerDocsUpdate'
import { IncorrectStoreHours } from './implementations/IncorrectStoreHours'
import { QasBatchAssigned } from './implementations/QasBatchAssigned'
import { QasBatchStateChanged } from './implementations/QasBatchStateChanged'
import { MockNotification } from './implementations/MockNotification'
import { MockPartnerNotification } from './implementations/MockPartnerNotification'
import { FlyerDataIngestionFailure } from './implementations/FlyerDataIngestionFailure'
import { FlyerDataIngestionSuccess } from './implementations/FlyerDataIngestionSuccess'
import { StoreHoursUpdatePartialFailure } from './implementations/StoreHoursUpdatePartialFailure'
import { QasBatchStateChangedNotifyReviewer } from './implementations/QasBatchStateChangedNotifyReviewer'
import { FlyerUploadExpectedNotReceived } from './implementations/FlyerUploadExpectedNotReceived'
import { PartnerFileInventoryFailureNotification } from './implementations/PartnerFileInventoryFailureNotification'
import { PartnerFileValidationRejectedRows } from './implementations/PartnerFileValidationRejectedRows'
import { RetailerFileValidationRejectedRows } from './implementations/RetailerFileValidationRejectedRows'
import { RetailerFileInventoryFailureNotification } from './implementations/RetailerFileInventoryFailureNotification'
import { RetailerDiscountThresholdExceeded } from './implementations/RetailerDiscountThresholdExceeded'
import { CampWorkflowJobDoneNotifyFollower } from './implementations/CampWorkflowJobDoneNotifyFollower'
import { IncentiveCampaignSpend } from './implementations/IncentiveCampaignSpend'
import { AggregatedIncentiveCampaignsSpend } from './implementations/AggregatedIncentiveCampaignsSpend'
import { CampaignCloseToServingLimit } from './implementations/CampaignCloseToServingLimit'
import { CampaignPausedByServingLimit } from './implementations/CampaignPausedByServingLimit'
import { BudgetExhausted } from './implementations/BudgetExhausted'
import { BulkShiftReadyForDownload } from './implementations/BulkShiftReadyForDownload'
import { BulkShiftUploadSuccess } from './implementations/BulkShiftUploadSuccess'
import { BulkShiftUploadFailure } from './implementations/BulkShiftUploadFailure'
import { BulkShiftUploadCompletedWithFailures } from './implementations/BulkShiftUploadCompletedWithFailures'
import { Cancelled1pDeliveryOrders } from './implementations/Cancelled1pDeliveryOrders'
import { PickingDelays1pDeliveryOrders } from './implementations/PickingDelays1pDeliveryOrders'
import { DriverDelays1pDeliveryOrders } from './implementations/DriverDelays1pDeliveryOrders'

type RenderParams<T> = {
  messageKey: string
  values?: Record<string, any>
}

export type TemplateDefinition<T = BaseTemplateProps & Record<string, any>> = {
  renderParams: (data: T) => RenderParams<T>
  getLink?: (data: T) => string
}

export enum NotificationTemplateType {
  ArConnectApiKeyAddedApprovalAndPublishNotifyFollowers = 'AR_CONNECT_API_KEY_ADDED_APPROVAL_AND_PUBLISH_NOTIFY_FOLLOWERS',
  ArConnectApiKeyAddedCancelNotifyFollowers = 'AR_CONNECT_API_KEY_ADDED_CANCEL_NOTIFY_FOLLOWERS',
  ArConnectApiKeyCreatedNotifyApprovers = 'AR_CONNECT_API_KEY_CREATED_NOTIFY_APPROVERS',
  ArConnectApiKeyCreatedNotifyAuthor = 'AR_CONNECT_API_KEY_CREATED_NOTIFY_AUTHOR',
  ArConnectApiKeyExpiryNotifyKeyManagers = 'AR_CONNECT_API_KEY_EXPIRY_NOTIFY_KEY_MANAGERS',
  ArConnectApiKeyPublishedNotifyFollowers = 'AR_CONNECT_API_KEY_PUBLISHED_NOTIFY_FOLLOWERS',
  ArFulfillmentEventsCreatedNotifyApprovers = 'AR_FULFILLMENT_EVENTS_CREATED_NOTIFY_APPROVERS',
  ArFulfillmentEventsCreatedNotifyAuthor = 'AR_FULFILLMENT_EVENTS_CREATED_NOTIFY_AUTHOR',
  ArFulfillmentEventsAddedCancelNotifyFollowers = 'AR_FULFILLMENT_EVENTS_ADDED_CANCEL_NOTIFY_FOLLOWERS',
  ArFulfillmentEventsPublishedNotifyFollowers = 'AR_FULFILLMENT_EVENTS_PUBLISHED_NOTIFY_FOLLOWERS',
  ArFulfillmentEventsAddedReviewNotifyFollowers = 'AR_FULFILLMENT_EVENTS_ADDED_REVIEW_NOTIFY_FOLLOWERS',
  IncorrectStoreHours = 'INCORRECT_STORE_HOURS',
  MockNotification = 'MOCK_NOTIFICATION_TEMPLATE',
  MockPartnerNotification = 'MOCK_PARTNER_NOTIFICATION_TEMPLATE',
  OAuthApplicationRotation = 'OAUTH_APPLICATION_ROTATION_NOTIFY_MANAGERS',
  PartnerDocsUpdate = 'PARTNER_DOCS_UPDATE',
  QasBatchAssigned = 'QAS_BATCH_ASSIGNED_NOTIFY_ASSIGNEE',
  QasBatchStateChanged = 'QAS_BATCH_STATE_CHANGED_NOTIFY_OWNER',
  QasBatchStateChangedNotifyReviewer = 'QAS_BATCH_STATE_CHANGED_NOTIFY_REVIEWER',
  RecipeFileFailure = 'RECIPE_FILE_FAILURE',
  RecipeFileReceived = 'RECIPE_FILE_RECEIVED',
  StoreHoursUpdateFailure = 'STORE_HOURS_UPDATE_FAILURE',
  StoreHoursUpdatePartialFailure = 'STORE_HOURS_UPDATE_PARTIAL_FAILURE',
  StoreHoursUpdateSuccess = 'STORE_HOURS_UPDATE_SUCCESS',
  ArYodaRulesCreatedNotifyApprovers = 'AR_YODA_RULES_CREATED_NOTIFY_APPROVERS',
  ArYodaRulesAddedApprovalAndPublishNotifyFollowers = 'AR_YODA_RULES_ADDED_APPROVAL_AND_PUBLISH_NOTIFY_FOLLOWERS',
  ArYodaRulesCreatedNotifyAuthor = 'AR_YODA_RULES_CREATED_NOTIFY_AUTHOR',
  ArYodaRulesAddedCancelNotifyFollowers = 'AR_YODA_RULES_ADDED_CANCEL_NOTIFY_FOLLOWERS',
  ArYodaRulesPublishedNotifyFollowers = 'AR_YODA_RULES_PUBLISHED_NOTIFY_FOLLOWERS',
  FlyerDataIngestionFailure = 'FLYER_DATA_INGESTION_FAILURE',
  FlyerDataIngestionSuccess = 'FLYER_DATA_INGESTION_SUCCESS',
  FlyerUploadExpectedNotReceived = 'FLYER_UPLOAD_EXPECTED_NOT_RECEIVED',
  PartnerFileInventoryFailureNotification = 'PARTNER_FILE_INVENTORY_FAILURE_NOTIFICATION',
  PartnerFileValidationRejectedRows = 'PARTNER_FILE_VALIDATION_REJECTED_ROWS',
  RetailerFileInventoryFailureNotification = 'RETAILER_FILE_INVENTORY_FAILURE_NOTIFICATION',
  RetailerFileValidationRejectedRows = 'RETAILER_FILE_VALIDATION_REJECTED_ROWS',
  RetailerDiscountThresholdExceeded = 'RETAILER_DISCOUNT_THRESHOLD_EXCEEDED',
  CampWorkflowJobDoneNotifyFollower = 'CAMP_WORKFLOW_JOB_DONE_NOTIFY_FOLLOWER',
  IncentiveCampaignSpend = 'INCENTIVE_CAMPAIGN_SPEND',
  AggregatedIncentiveCampaignsSpend = 'AGGREGATED_INCENTIVE_CAMPAIGNS_SPEND',
  CampaignCloseToServingLimit = 'CAMPAIGN_CLOSE_TO_SERVING_LIMIT',
  CampaignPausedByServingLimit = 'CAMPAIGN_PAUSED_BY_SERVING_LIMIT',
  BudgetExhausted = 'BUDGET_EXHAUSTED',
  BulkShiftReadyForDownload = 'BULK_SHIFT_READY_FOR_DOWNLOAD',
  BulkShiftUploadSuccess = 'BULK_SHIFT_UPLOAD_COMPLETE',
  BulkShiftUploadFailure = 'BULK_SHIFT_UPLOAD_FAILED',
  BulkShiftUploadCompletedWithFailures = 'BULK_SHIFT_UPLOAD_COMPLETED_WITH_FAILURES',
  Cancelled1pDeliveryOrders = 'CANCELLED_1P_DELIVERY_ORDERS',
  PickingDelays1pDeliveryOrders = 'PICKING_DELAYS_1P_DELIVERY_ORDERS',
  DriverDelays1pDeliveryOrders = 'DRIVER_DELAYS_1P_DELIVERY_ORDERS',
}

const templateRegistry: Record<
  NotificationTemplateType,
  TemplateDefinition<any> | ((data: any) => string) | any
> = {
  [NotificationTemplateType.ArConnectApiKeyAddedApprovalAndPublishNotifyFollowers]:
    ArConnectApiKeyAddedApprovalAndPublishNotifyFollowers,
  [NotificationTemplateType.ArConnectApiKeyAddedCancelNotifyFollowers]:
    ArConnectApiKeyAddedCancelNotifyFollowers,
  [NotificationTemplateType.ArConnectApiKeyCreatedNotifyApprovers]:
    ArConnectApiKeyCreatedNotifyApprovers,
  [NotificationTemplateType.ArConnectApiKeyCreatedNotifyAuthor]: ArConnectApiKeyCreatedNotifyAuthor,
  [NotificationTemplateType.ArConnectApiKeyExpiryNotifyKeyManagers]:
    ArConnectApiKeyExpiryNotifyKeyManagers,
  [NotificationTemplateType.ArConnectApiKeyPublishedNotifyFollowers]:
    ArConnectApiKeyPublishedNotifyFollowers,
  [NotificationTemplateType.ArFulfillmentEventsCreatedNotifyApprovers]:
    ArFulfillmentEventsCreatedNotifyApprovers,
  [NotificationTemplateType.ArFulfillmentEventsCreatedNotifyAuthor]:
    ArFulfillmentEventsCreatedNotifyAuthor,
  [NotificationTemplateType.ArFulfillmentEventsAddedCancelNotifyFollowers]:
    ArFulfillmentEventsAddedCancelNotifyFollowers,
  [NotificationTemplateType.ArFulfillmentEventsPublishedNotifyFollowers]:
    ArFulfillmentEventsPublishedNotifyFollowers,
  [NotificationTemplateType.ArFulfillmentEventsAddedReviewNotifyFollowers]:
    ArFulfillmentEventsAddedReviewNotifyFollowers,
  [NotificationTemplateType.IncorrectStoreHours]: IncorrectStoreHours,
  [NotificationTemplateType.MockNotification]: MockNotification,
  [NotificationTemplateType.MockPartnerNotification]: MockPartnerNotification,
  [NotificationTemplateType.OAuthApplicationRotation]: OAuthApplicationRotation,
  [NotificationTemplateType.PartnerDocsUpdate]: PartnerDocsUpdate,
  [NotificationTemplateType.QasBatchAssigned]: QasBatchAssigned,
  [NotificationTemplateType.QasBatchStateChanged]: QasBatchStateChanged,
  [NotificationTemplateType.QasBatchStateChangedNotifyReviewer]: QasBatchStateChangedNotifyReviewer,
  [NotificationTemplateType.RecipeFileFailure]: RecipeFileFailure,
  [NotificationTemplateType.RecipeFileReceived]: RecipeFileReceived,
  [NotificationTemplateType.StoreHoursUpdateFailure]: StoreHoursUpdateFailure,
  [NotificationTemplateType.StoreHoursUpdatePartialFailure]: StoreHoursUpdatePartialFailure,
  [NotificationTemplateType.StoreHoursUpdateSuccess]: StoreHoursUpdateSuccess,
  [NotificationTemplateType.ArYodaRulesCreatedNotifyApprovers]: ArYodaRulesCreatedNotifyApprovers,
  [NotificationTemplateType.ArYodaRulesAddedApprovalAndPublishNotifyFollowers]:
    ArYodaRulesAddedApprovalAndPublishNotifyFollowers,
  [NotificationTemplateType.ArYodaRulesCreatedNotifyAuthor]: ArYodaRulesCreatedNotifyAuthor,
  [NotificationTemplateType.ArYodaRulesAddedCancelNotifyFollowers]:
    ArYodaRulesAddedCancelNotifyFollowers,
  [NotificationTemplateType.ArYodaRulesPublishedNotifyFollowers]:
    ArYodaRulesPublishedNotifyFollowers,
  [NotificationTemplateType.FlyerDataIngestionFailure]: FlyerDataIngestionFailure,
  [NotificationTemplateType.FlyerDataIngestionSuccess]: FlyerDataIngestionSuccess,
  [NotificationTemplateType.FlyerUploadExpectedNotReceived]: FlyerUploadExpectedNotReceived,
  [NotificationTemplateType.RetailerFileInventoryFailureNotification]:
    RetailerFileInventoryFailureNotification,
  [NotificationTemplateType.RetailerFileValidationRejectedRows]: RetailerFileValidationRejectedRows,
  [NotificationTemplateType.PartnerFileInventoryFailureNotification]:
    PartnerFileInventoryFailureNotification,
  [NotificationTemplateType.PartnerFileValidationRejectedRows]: PartnerFileValidationRejectedRows,
  [NotificationTemplateType.RetailerDiscountThresholdExceeded]: RetailerDiscountThresholdExceeded,
  [NotificationTemplateType.CampWorkflowJobDoneNotifyFollower]: CampWorkflowJobDoneNotifyFollower,
  [NotificationTemplateType.IncentiveCampaignSpend]: IncentiveCampaignSpend,
  [NotificationTemplateType.AggregatedIncentiveCampaignsSpend]: AggregatedIncentiveCampaignsSpend,
  [NotificationTemplateType.CampaignCloseToServingLimit]: CampaignCloseToServingLimit,
  [NotificationTemplateType.CampaignPausedByServingLimit]: CampaignPausedByServingLimit,
  [NotificationTemplateType.BudgetExhausted]: BudgetExhausted,
  [NotificationTemplateType.BulkShiftReadyForDownload]: BulkShiftReadyForDownload,
  [NotificationTemplateType.BulkShiftUploadSuccess]: BulkShiftUploadSuccess,
  [NotificationTemplateType.BulkShiftUploadFailure]: BulkShiftUploadFailure,
  [NotificationTemplateType.BulkShiftUploadCompletedWithFailures]:
    BulkShiftUploadCompletedWithFailures,
  [NotificationTemplateType.Cancelled1pDeliveryOrders]: Cancelled1pDeliveryOrders,
  [NotificationTemplateType.PickingDelays1pDeliveryOrders]: PickingDelays1pDeliveryOrders,
  [NotificationTemplateType.DriverDelays1pDeliveryOrders]: DriverDelays1pDeliveryOrders,
}

export const useRenderTemplate = (
  templateType: NotificationTemplateType,
  data: BaseTemplateProps & Record<string, any>
): string => {
  const template = templateRegistry[templateType]

  // Determine message key and values
  let messageKey = ''
  let values: Record<string, any> = {}

  if (template) {
    const renderParams = template.renderParams(data)
    messageKey = renderParams.messageKey
    values = renderParams.values || {}
  }

  // Always call the hook unconditionally
  const message = useDomainMessage(messageKey as any, values)
  return message || ''
}

export const getLink = (
  templateType: NotificationTemplateType,
  data: BaseTemplateProps & Record<string, any>
): string => {
  const template = templateRegistry[templateType]
  return template?.getLink?.(data) || ''
}
